import { ObjectByString, getValue } from "@/shared/utils/helpers";
import { useCallback, useEffect, useMemo } from "react";
import useTableIds from "../Tables/useTableIds";
import { toggle } from "./helpers";
import { BaseRow, TableId } from "./types";

interface Props {
  id: TableId;
  data: BaseRow[];
  loseSelectOnDataChange: boolean;
}
const useTableSelectHook = ({ id, data, loseSelectOnDataChange }: Props) => {
  const [ids, setIds] = useTableIds();
  const isAllActived = useMemo(
    () =>
      data.length === 0
        ? false
        : ids.filter(
            (item) =>
              !(typeof item === "object" && Object.keys(item).length === 0)
          ).length === data.filter((row) => !row._group).length,
    [data, ids]
  );

  const setAll = useCallback(() => {
    setIds(() =>
      ids.length === 0 || !isAllActived
        ? data.map((d) => getValue(ObjectByString(d, id.toString())))
        : []
    );
  }, [ids.length, id, data, isAllActived]);

  const handleSet = useCallback(
    (value: number) => {
      setIds(toggle(ids, value));
    },
    [ids]
  );

  const isGroupActive = (group_id: string) => {
    const groupData = data
      .filter((row) => row._group_id == group_id)
      .map((row) => ObjectByString(row, `${id}`));
    return groupData.every((id) => ids.includes(id));
  };

  const setGroupActive = (group_id: string) => {
    const isActive = isGroupActive(group_id);
    const groupData = data
      .filter((row) => row._group_id == group_id)
      .map((row) => ObjectByString(row, `${id}`));
    return setIds((current) =>
      isActive
        ? current.filter((id) => !groupData.includes(id))
        : [...current, ...groupData]
    );
  };

  const isActive = (id: number) => ids.includes(id);

  useEffect(() => {
    setIds([]);

    return () => {
      setIds([]);
    };
  }, [id]);

  useEffect(() => {
    if (loseSelectOnDataChange) {
      setIds([]);
    }
  }, [data, loseSelectOnDataChange]);

  return {
    isGroupActive,
    setGroupActive,
    ids,
    setIds,
    setAll,
    handleSet,
    isActive,
    isAllActived,
  };
};

export default useTableSelectHook;
